import apiClient from "./index";

/**
 * Get all managers with pagination.
 * @param {number} limit - Number of records to fetch.
 * @param {string|null} lastEvaluatedKey - Last evaluated key for pagination.
 */
export const getAllManagers = async (limit = 10, lastEvaluatedKey = null) => {
  try {
    const { data } = await apiClient.get("/managers", {
      params: {
        limit,
        lastEvaluatedKey,
      },
    });
    return data; // Should return { managers: [...], pagination: {...} }
  } catch (error) {
    console.error("Failed to fetch managers:", error);
    throw new Error(error.response?.data?.message || "Error fetching managers");
  }
};

export const createManager = async (manager) => {
  try {
    const { data } = await apiClient.post("/managers", manager);
    return data.manager; // Return the manager object from the response
  } catch (error) {
    console.error("Failed to create manager:", error);
    throw new Error(error.response?.data?.message || "Error creating manager");
  }
};

/**
 * Update an existing manager.
 * @param {string} managerId - ID of the manager to update.
 * @param {Object} updates - Updates to apply to the manager.
 * @param {string} [updates.username] - Updated username of the manager.
 * @param {string} [updates.email] - Updated email of the manager.
 * @param {string} [updates.name] - Updated full name of the manager.
 */
export const updateManager = async (managerId, updates) => {
  try {
    const { data } = await apiClient.put(`/managers/${managerId}`, {
      ...(updates.username && { username: updates.username }),
      ...(updates.email && { email: updates.email }),
      ...(updates.name && { name: updates.name }),
    });
    return data; // Should return the updated manager object
  } catch (error) {
    console.error("Failed to update manager:", error);
    throw new Error(error.response?.data?.message || "Error updating manager");
  }
};

/**
 * Delete a manager by ID.
 * @param {string} managerId - ID of the manager to delete.
 */
export const deleteManager = async (managerId) => {
  try {
    await apiClient.delete(`/managers/${managerId}`);
    return { success: true }; // Should return success response
  } catch (error) {
    console.error("Failed to delete manager:", error);
    throw new Error(error.response?.data?.message || "Error deleting manager");
  }
};

/**
 * Get all pending approval activities.
 * @returns {Promise<Object[]>} A promise that resolves to the list of pending activities.
 */
export const pendingApprovalActivities = async () => {
  try {
    const { data } = await apiClient.get("/pending-approval-activities");
    return data; // Should return { pendingActivities: [...] }
  } catch (error) {
    console.error("Failed to fetch pending activities:", error);
    throw new Error(error.response?.data?.message || "Error fetching pending activities");
  }
};

/**
 * Approve an activity by ID.
 * @param {string} activityId - ID of the activity to approve.
 * @returns {Promise<void>} Resolves on success.
 */
export const approveActivity = async (activityId) => {
  try {
    await apiClient.patch(`/approve-activity/${activityId}`);
  } catch (error) {
    console.error("Failed to approve activity:", error);
    throw new Error(
      error.response?.data?.message || "Error approving activity"
    );
  }
};